import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Trash2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FindoSpinner } from "@/components/FindoSpinner";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

function StatusBadge({ r }) {
  if (r.status === "ok") return <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"><CheckCircle2 size={12} /> Conforme</span>;
  return r.alert_dismissed
    ? <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Écart traité</span>
    : <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-findo-danger/10 text-findo-danger"><AlertTriangle size={12} /> Écart</span>;
}

function AlertBanner({ r, onDismiss }) {
  const sign = r.diff_eur > 0 ? "+" : "";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-findo-red/30 bg-findo-red/5 px-4 py-3" data-testid={`sendcloud-alert-${r.month}`}>
      <AlertTriangle size={18} className="text-findo-danger shrink-0" />
      <p className="text-sm text-slate-800 flex-1 min-w-[240px]">
        <b>Écart sur la facture Sendcloud de {monthLabel(r.month)}</b> : facture {eur(r.amount_ht)} HT vs coût calculé {eur(r.computed_cost)} ({sign}{eur(r.diff_eur)}, {sign}{r.diff_pct} %).
        {r.has_estimated && <span className="text-amber-700"> Contient des coûts estimés.</span>}
      </p>
      <Button size="sm" variant="outline" className="rounded-full font-semibold" onClick={() => onDismiss(r.month)} data-testid={`sendcloud-alert-dismiss-${r.month}`}>
        <Check size={14} className="mr-1" /> Marquer comme traité
      </Button>
    </div>
  );
}

export default function SendcloudReconciliation() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(lastMonth());
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api("/admin/sendcloud-reconciliation").then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    const v = parseFloat(String(amount).replace(",", "."));
    if (!month || Number.isNaN(v) || v < 0) { toast.error("Indiquez un mois et un montant HT valide"); return; }
    setSaving(true);
    try {
      const r = await api(`/admin/sendcloud-reconciliation/${month}`, { method: "PUT", body: { amount_ht: v } });
      toast[r.status === "ok" ? "success" : "warning"](r.status === "ok" ? "Facture conforme au coût calculé" : `Écart détecté : ${eur(r.diff_eur)}`);
      setAmount(""); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  }
  async function dismiss(m) {
    try { await api(`/admin/sendcloud-reconciliation/${m}/dismiss`, { method: "POST" }); toast.success("Écart marqué comme traité"); load(); }
    catch (err) { toast.error(err.message); }
  }
  async function remove(m) {
    if (!window.confirm(`Supprimer la facture saisie pour ${monthLabel(m)} ?`)) return;
    try { await api(`/admin/sendcloud-reconciliation/${m}`, { method: "DELETE" }); load(); } catch (err) { toast.error(err.message); }
  }

  const alerts = (data?.invoices || []).filter((r) => r.status === "mismatch" && !r.alert_dismissed);
  return (
    <section id="sendcloud-reconciliation" className="bg-white rounded-2xl border border-border card-shadow p-5 mb-6" data-testid="sendcloud-reconciliation">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display font-bold text-lg text-findo-navy">Rapprochement Sendcloud</h3>
        {alerts.length > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-findo-red text-white" data-testid="sendcloud-alert-count">{alerts.length} écart{alerts.length > 1 ? "s" : ""}</span>}
      </div>
      <p className="text-sm text-slate-500">Saisissez le montant HT de la facture Sendcloud du mois : il est comparé au coût calculé par findloop (alerte au-delà de {data?.tolerance_eur ?? 1} € ou {data?.tolerance_pct ?? 2} %).</p>

      {alerts.length > 0 && <div className="mt-4 space-y-2">{alerts.map((r) => <AlertBanner key={r.month} r={r} onDismiss={dismiss} />)}</div>}

      <form onSubmit={save} className="mt-4 flex flex-wrap items-end gap-3" data-testid="sendcloud-invoice-form">
        <label className="text-sm font-semibold text-slate-700">Mois
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-44" data-testid="sendcloud-invoice-month" />
        </label>
        <label className="text-sm font-semibold text-slate-700">Montant facture HT (€)
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="ex. 124,50" className="mt-1 w-44" data-testid="sendcloud-invoice-amount" />
        </label>
        <Button type="submit" disabled={saving} className="rounded-full font-semibold" data-testid="sendcloud-invoice-save">
          {saving && <FindoSpinner size={14} className="mr-1.5" />} Comparer et enregistrer
        </Button>
      </form>

      {data?.invoices?.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="sendcloud-invoices-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2">Mois</th><th className="px-3 py-2 text-right">Facture HT</th><th className="px-3 py-2 text-right">Coût calculé</th><th className="px-3 py-2 text-right">Écart</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody>
              {data.invoices.map((r) => (
                <tr key={r.month} className="border-t border-border" data-testid={`sendcloud-invoice-row-${r.month}`}>
                  <td className="px-3 py-2 font-semibold capitalize">{monthLabel(r.month)}<span className="block text-xs font-normal text-slate-400 normal-case">{r.shipments} expédition{r.shipments > 1 ? "s" : ""}</span></td>
                  <td className="px-3 py-2 text-right">{eur(r.amount_ht)}</td>
                  <td className="px-3 py-2 text-right">{eur(r.computed_cost)}{r.has_estimated && <span className="block text-xs text-amber-700">dont estimé</span>}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${r.status === "ok" ? "text-slate-700" : "text-findo-red"}`}>{r.diff_eur > 0 ? "+" : ""}{eur(r.diff_eur)} <span className="text-xs font-normal">({r.diff_pct > 0 ? "+" : ""}{r.diff_pct} %)</span></td>
                  <td className="px-3 py-2"><StatusBadge r={r} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setMonth(r.month); setAmount(String(r.amount_ht).replace(".", ",")); }} className="text-xs font-semibold text-findo-indigo hover:underline mr-3" data-testid={`sendcloud-invoice-edit-${r.month}`}>Modifier</button>
                    <button onClick={() => remove(r.month)} className="text-slate-400 hover:text-findo-danger" aria-label="Supprimer" data-testid={`sendcloud-invoice-delete-${r.month}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
